import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import './ProductList.scss';
import buttonListViewSelected from '../../assets/button_list_view_selected.svg';
import buttonListView from '../../assets/button_list_view.svg';
import buttonThumbnailViewSelected from '../../assets/button_thumbnail_view_selected.svg';
import buttonThumbnailView from '../../assets/button_thumbnail_view.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import useCartStore from './../../store/cartStore'; 
import useUserStore from '../../store/userStore'; 
import useProductStore from '../../store/productStore';
import { Product } from '../../types/product';
import useCategoryStore from '../../store/categoryStore';
import { Category } from '../../types/Category';
import { useLoadingStore } from '../../store/loadingStore';
import { createCartItemDTO } from '../../types/Cart';
import useToastStore from '../../store/toastStore';
import ProductTopBar from '../Filters/ProductTopBar';
import ProductFilters from '../Filters/ProductFilters';

const ProductList = () => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [quantities, setQuantities] = useState<{ [sku: string]: number }>({});
  const [categoryName, setCategoryName] = useState<string | null>(null);
  const [brandImage, setBrandImage] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<string>('lowToHigh');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated ,user} = useUserStore();
  const { addToCart } = useCartStore();
  const{brands,categories} = useCategoryStore();
  const {setLoading} = useLoadingStore();
  const {showToast} = useToastStore();
  const [isMobile, setIsMobile] = useState(false);
  const ITEMS_PER_PAGE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  // Use product store
  const {
    products,
    fetchProducts,
  } = useProductStore();


  const categoryId = searchParams.get('c');
  const brandId = searchParams.get('b');


  useEffect(() => {
    const request = async ()=>{
      setLoading(true)
      await fetchProducts({brandId:brandId,categoryId:categoryId});
      setLoading(false)
    }
    request();
  
  }, [categoryId,brandId,user]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1100);
    };

    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Set category name when categoryId changes
  useEffect(() => {
    if (categoryId) {
      const flattenedCategories = categories.flatMap((cat) => [
        cat,
        ...(cat.children || []),
      ]);
      const category = flattenedCategories.find(
        (cat) => cat.id === categoryId
      );
      setCategoryName(category?.name || null);
    } else {
      setCategoryName(null);
    }
  }, [categoryId]);

  // Set brand image when brandId changes
  useEffect(() => {
    if (brandId) {
      const brand = brands.find((b) => b.id ===brandId);
      setBrandImage(brand?.imageUrl || null);
    } else {
      setBrandImage(null);
    }
  }, [brandId]);

  

  const displayedProducts = useMemo(() => {


    // Apply sorting - create new array before sorting to avoid mutating original
    const sortedProducts = [...products];
    switch (sortOrder) {
      case 'lowToHigh':
        return sortedProducts.sort((a, b) => a.b2cPrice - b.b2cPrice);
      case 'highToLow':
        return sortedProducts.sort((a, b) => b.b2cPrice - a.b2cPrice);
      case 'aToZ':
        return sortedProducts.sort((a, b) => a.name.localeCompare(b.name));
      case 'zToA':
        return sortedProducts.sort((a, b) => b.name.localeCompare(a.name));
      default:
        return sortedProducts;
    }
  }, [products, categoryId, brandId, sortOrder]);

  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return displayedProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [displayedProducts, currentPage]);

  const getCategoriesToDisplay = useCallback((): Category[] => {
    console.log(categories)
    return categories;
    // if (!categories || categories.length === 0) return [];
  
    // // No category selected - show only top-level root categories
    // if (!categoryId) {
    //   return categories.filter(cat => 
    //     // Either has parentId === rootId (flat structure root)
    //     (cat.parentId && cat.parentId === cat.rootId) ||
    //     // Or has no parentId but is top-level (nested structure root)
    //     (!cat.parentId && !cat.rootId)
    //   );
    // }
  
    // // Find selected category (search through all levels)
    // const selectedCategory = categories.find(cat => cat.id === categoryId) || 
    //                        categories.flatMap(cat => cat.children || []).find(child => child.id === categoryId);
  
    // if (!selectedCategory) return [];
  
    // // Should we show children? Only if:
    // // 1. This is a root category (parentId === rootId) OR top-level in nested structure
    // // 2. We're not showing grandchildren (level 3)
    // const shouldShowChildren = 
    //   (selectedCategory.parentId === selectedCategory.rootId) || // Flat root
    //   (!selectedCategory.parentId && !selectedCategory.rootId); // Nested root
  
    // if (shouldShowChildren && selectedCategory.children?.length > 0) {
    //   return selectedCategory.children;
    // }
  
    // // If this is a child category (level 2), show its siblings
    // if (selectedCategory.parentId && selectedCategory.parentId !== selectedCategory.rootId) {
    //   // Find all siblings (including self)
    //   return categories.flatMap(cat => 
    //     cat.children?.filter(child => child.parentId === selectedCategory.parentId) || []
    //   );
    // }
  
    // // If this is a root category with no children to show
    // return [];
  }, [categoryId, categories]);

  const totalPages = Math.ceil(displayedProducts.length / ITEMS_PER_PAGE);

  const handleCategorySelect = (categoryId: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (categoryId) {
      params.set('c', categoryId.toString());
    }else{
      params.delete('c');
    }
    //params.delete('b');
    navigate(`/products?${params.toString()}`);
  };

  const handleBrandSelect = (brandId: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (brandId) {
      params.set('b', brandId.toString());
    } else {
      params.delete('b');
    }
    navigate(`/products?${params.toString()}`);
  };

  const handleSortChange = (value: string) => {
    setSortOrder(value);
  };

  const handleQuantityChange = (sku: string, quantity: number) => {
    setQuantities((prev) => ({
      ...prev,
      [sku]: Math.max((prev[sku] || 0) + quantity, 0),
    }));
  };

  const handleAddToCart = async (product: Product) => {
    const cartItem = createCartItemDTO();
    cartItem.productId = product?.id;
    cartItem.price = product?.b2bPrice;
    cartItem.name = product?.name;
    cartItem.image = product.banner;
    cartItem.quantity= quantities[product.sku] || 1;
    setLoading(true);
    try{
      await addToCart(cartItem);
      setQuantities((prev) => ({
        ...prev,
        [product.sku]: 0,
      }));
      showToast('Added product to cart!','success')
    }
    catch(error){
      showToast('Error product to cart!','error')
    }
    setLoading(false);
   
  };


  return (
    <div className="product-list">
    

      {/* Top Category */}
      {categoryName && <div className="top-category">{categoryName}</div>}

      {/*Top Bar*/}
      <ProductTopBar
        categoryId={categoryId}
        brandId={brandId}
        sortOrder={sortOrder}
        categories={categories}
        brands={brands}
        getCategoriesToDisplay={getCategoriesToDisplay}
        onCategoryChange={handleCategorySelect}
        onBrandChange={handleBrandSelect}
        onSortChange={handleSortChange}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      /> 
      <div className='products-outer-wrap'>
        <div className='products-filters'>
        <ProductFilters
          categoryId={categoryId}
          brandId={brandId}
          sortOrder={sortOrder}
          categories={categories}
          brands={brands}
          onCategoryChange={handleCategorySelect}
          onBrandChange={handleBrandSelect}
          onSortChange={handleSortChange}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        /> 
        </div>
        {/* Products */}
        <div className={`products ${viewMode}`}>
          {paginatedProducts.map((product) => (
            <div key={`${product?.id}`} className="product-item">
              {viewMode === 'grid' ? (
                // Grid View
                <div className="grid-view"  onClick={() => navigate(`/product/${product?.sku}`)}>
                  <div className="productImage">
                    <img
                      src={product?.banner}
                      alt={product?.name}
                      className="banner"
                    
                    />
                  </div>
                  <div className="info">
                    <h3>{product?.name}</h3>
                    <div className="sku-row">
                      <div className="sku">SKU: {product?.sku}</div>
                      {product?.salesPercentage > 0 && (
                        <div className="percent">-{product.salesPercentage}%</div>
                      )}
                    </div>
                    <div className={`status ${product?.status?.replace(/\s+/g, '-').toLowerCase()}`}>
                      {product?.status}
                    </div>
                    <div className="prices">
                      {product?.inContract ? (
                        <>
                          <span className="b2b-price">{product?.b2bPrice?.toFixed(2)}€</span>
                          <span className="b2c-price struck">{product?.b2cPrice?.toFixed(2)}€</span>
                        </>
                      ) : (
                        <span className="b2c-price">{product?.b2cPrice?.toFixed(2)}€</span>
                      )}
                    </div>
                    <div className="hover-overlay">
                      <div className="quant">Quantity</div>
                      <div className="quantity-controls">
                        <button
                          className="quanBtn"
                          onClick={(e) => {
                            e.stopPropagation(); 
                            handleQuantityChange(product?.sku, -1);
                          }}
                        >
                          -
                        </button>
                        <span>{quantities[product?.sku] || 0}</span>
                        <button
                          className="quanBtn"
                          onClick={(e) => {
                            e.stopPropagation(); 
                            handleQuantityChange(product?.sku, 1);
                          }}
                        >
                          +
                        </button>
                      </div>
                      <button
                        className="add-to-cart"
                        disabled={!isAuthenticated || !product?.inContract}
                        onClick={(e) => {
                          e.stopPropagation(); 
                          handleAddToCart(product);
                        }}
                        
                      >
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // List View
                <div className="list-view">
                  <img
                    src={product?.banner}
                    alt={product?.name}
                    className="image"
                    onClick={() => navigate(`/product/${product?.sku}`)}
                  />
                  <div className="details">
                    <h3>{product?.name}</h3>
                    <div className="sku-row">
                      <div className="sku">SKU: {product?.sku}</div>
                      {product?.salesPercentage != null && (
                        <div className="percent">-{product.salesPercentage}%</div>
                      )}
                    </div>
                    {isMobile && <div className={`status ${product?.status?.replace(/\s+/g, '-').toLowerCase()}`}>
                    {product?.status}
                  </div>}
                      {isMobile && <div className="prices">
                      {product?.inContract ? (
                        <>
                          <span className="b2c-price struck">{product?.b2cPrice?.toFixed(2)}€</span>
                          <span className="b2b-price">{product?.b2bPrice?.toFixed(2)}€</span>
                        </>
                      ) : (
                        <span className="b2c-price">{product?.b2cPrice.toFixed(2)}€</span>
                      )}
                    </div>}
                  </div>
                  {!isMobile && <div className="prices">
                    {product?.inContract ? (
                      <>
                        <span className="b2c-price struck">{product?.b2cPrice?.toFixed(2)}€</span>
                        <span className="b2b-price">{product?.b2bPrice?.toFixed(2)}€</span>
                      </>
                    ) : (
                      <span className="b2c-price">{product?.b2cPrice.toFixed(2)}€</span>
                    )}
                  </div>}
                  {!isMobile && <div className={`status ${product?.status?.replace(/\s+/g, '-').toLowerCase()}`}>
                    {product?.status}
                  </div>}
                  {!isMobile && <div className="cartCol">
                    <div className="quant">Quantity</div>
                    <div className="addCart">
                      <div className="quantity-controls">
                        <button
                          className="quanBtn"
                          onClick={() => handleQuantityChange(product?.sku, -1)}
                        >
                          -
                        </button>
                        <span>{quantities[product?.sku] || 0}</span>
                        <button
                          className="quanBtn"
                          onClick={() => handleQuantityChange(product?.sku, 1)}
                        >
                          +
                        </button>
                      </div>
                      <button
                        className="add-to-cart"
                        disabled={!isAuthenticated || !product?.inContract}
                        onClick={() => handleAddToCart(product)}
                      >
                        Add to Cart
                      </button>
                    </div>
              
                  </div>}
                
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="pagination-controls">
      <div className="page-navigation">
  <button
    onClick={() => setCurrentPage(1)}
    disabled={currentPage === 1}
    title="First Page"
  >
    &lt;&lt;
  </button>
  <button
    onClick={() => setCurrentPage(prev => Math.max(prev - 2, 1))}
    disabled={currentPage === 1}
    title="Previous Page"
  >
    &lt;
  </button>


  {currentPage > 1 && (
    <button onClick={() => setCurrentPage(currentPage - 1)}>
      {currentPage - 1}
    </button>
  )}

  <button className="active" disabled>
    {currentPage}
  </button>


  {currentPage < totalPages && (
    <button onClick={() => setCurrentPage(currentPage + 1)}>
      {currentPage + 1}
    </button>
  )}

  <button
    onClick={() => setCurrentPage(prev => Math.min(prev + 2, totalPages))}
    disabled={currentPage === totalPages}
    title="Next Page"
  >
    &gt;
  </button>

  {/* Last Page Button */}
  <button
    onClick={() => setCurrentPage(totalPages)}
    disabled={currentPage === totalPages}
    title="Last Page"
  >
    &gt;&gt;
  </button>
</div>
</div>
    </div>
  );
};

export default ProductList;